<template>
  <div class="dd">
    <div class="dd__container">
      <!-- Dropzone -->
      <div 
        v-bind="getRootProps()" 
        :class="['dropzone', { 'active-dropzone': isDragActive }]"
      >
        <input v-bind="getInputProps()" />
        <div class="dropzone-content">
          <CloudUploadIcon class="icon" />
          <p v-if="isDragActive">Отпустите файлы здесь</p>
          <p v-else>
            Перетащите файлы сюда или 
            <span class="link" @click.stop="open">выберите</span>
          </p>
          <small>
            Поддерживается: 
            <span v-if="allowedText">{{ allowedText }}</span>
            <span v-else>Все форматы</span>
            <br>
            <span>(макс. {{ maxSizeMB }}MB, макс. {{ maxFiles }} файлов)</span>
          </small>
        </div>
      </div>

      <!-- Список файлов -->
      <div v-if="files.length" class="file-list">
        <div v-for="(file, index) in files" :key="file.raw.name + index" class="file-item">
          <div class="file-info">
            <div class="file-preview-wrapper">
              <img 
                v-if="file?.preview || ((file.status === 'Успешно' || file.preview) && isImageFileObj(file.raw))" 
                :src="file?.preview || ( file.preview || (resImgPatch + file?.response?.url))" 
                class="file-preview" 
                alt="preview"
              />
              <div v-else-if="file.status === 'Загрузка...'" class="loader"></div>
              <FileIcon v-else class="file-icon" />
            </div>
            <div>
              <div class="file-name">{{ file.raw.name }}</div>
              <div :class="['file-status', file.status === 'Успешно' ? 'success' : file.status === 'Ошибка' ? 'error' : 'uploading']">
                {{ file.status }}
              </div>
              <div v-if="file.error" class="file-error">{{ file.error }}</div>
            </div>
          </div>

          <div class="file-actions">
            <button
              class="delete-btn"
              @click.stop="removeFile(index)"
              :title="'Удалить ' + (file.raw?.name || 'файл')"
              aria-label="Удалить файл"
              v-if="showDelete"
            >
              <Icon 
                name="close"
                size="16"
              />
            </button>

            <!-- показываем чекбокс выбора только если проп showSelect === true -->
            <label v-if="showSelect" class="checkbox-wrapper">
              <input 
                type="checkbox" 
                :checked="file.selected" 
                @change="selectFile(index)" 
              />
              <span class="custom-checkbox"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { useDropzone } from "vue3-dropzone";
import axios from "axios";

const CloudUploadIcon = { template: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>' };
const FileIcon = { template: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>' };

export default {
  name: "Upload",
  components: { CloudUploadIcon, FileIcon },
  props: {
    initialFiles: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * acceptTypes:
     * - null / undefined -> разрешены все форматы
     * - строка -> передаётся напрямую в accept (например "image/png,image/jpeg,.jpg")
     * - массив -> объединяется в string ([".svg","image/svg+xml"])
     */
    acceptTypes: {
      type: [Array, String],
      default: null
    },
    maxSizeMB: {
      type: Number,
      default: 10
    },
    showDelete: {
      type: Boolean,
      default: false
    },
    // Добавленный проп: showSelect (по умолчанию true)
    showSelect: {
      type: Boolean,
      default: true
    },
    // Новый проп: максимальное количество файлов (по умолчанию 3)
    maxFiles: {
      type: Number,
      default: 3
    }
  },
  emits: ["update:selectedFiles","update:selected-files","selectedFiles","selected-files"],
  setup(props, { emit }) {
    const files = ref([]);
    const uploadUrl = "https://sitebypro-server.ru/static/upload";
    const resImgPatch = "https://sitebypro-server.ru/static";
    const maxSizeBytes = props.maxSizeMB * 1024 * 1024;

    // compute accept string for dropzone
    const acceptString = computed(() => {
      if (!props.acceptTypes) return undefined;
      if (Array.isArray(props.acceptTypes)) return props.acceptTypes.join(',');
      return String(props.acceptTypes);
    });

    const allowedText = computed(() => {
      if (!props.acceptTypes) return '';
      if (Array.isArray(props.acceptTypes)) return props.acceptTypes.join(', ');
      return String(props.acceptTypes);
    });

    // Проверка изображения (для превью): если MIME начинается с image/ или расширение - изображение
    const isImageFileObj = (file) => {
      if (!file) return false;
      const name = String(file.name || '').toLowerCase();
      const type = String(file.type || '').toLowerCase();
      if (type.startsWith('image/')) return true;
      return /\.(png|jpe?g|gif|svg|webp|avif)$/.test(name);
    };

    // проверяет соответствует ли файл списку acceptTypes (если acceptTypes не указан — true)
    const isAcceptedFile = (file) => {
      if (!props.acceptTypes) return true; // всё разрешено
      const accepts = Array.isArray(props.acceptTypes)
        ? props.acceptTypes.map(a => String(a).trim())
        : String(props.acceptTypes).split(',').map(a => a.trim()).filter(Boolean);

      const name = String(file.name || '').toLowerCase();
      const type = String(file.type || '').toLowerCase();

      for (const a of accepts) {
        if (!a) continue;
        if (a.startsWith('.')) {
          if (name.endsWith(a.toLowerCase())) return true;
          continue;
        }
        if (a.endsWith('/*')) {
          // image/* style
          const prefix = a.slice(0, a.indexOf('/')).toLowerCase();
          if (type && type.startsWith(prefix + '/')) return true;
          // if type empty, try extension fallback later
          continue;
        }
        // exact mime match
        if (type && type === a.toLowerCase()) return true;
        // some browsers give "image/svg+xml; charset=utf-8" — include check
        if (type && type.includes(a.toLowerCase())) return true;
      }

      // fallback: check by extension if none matched mime
      for (const a of accepts) {
        if (!a) continue;
        if (a.startsWith('.')) {
          if (name.endsWith(a.toLowerCase())) return true;
        } else if (a.includes('/')) {
          // mime -> extract extension guess
          const extGuess = a.split('/')[1];
          if (extGuess && name.endsWith('.' + extGuess)) return true;
        }
      }

      return false;
    };

    const resolveUrl = (maybeUrl) => {
      if (!maybeUrl) return null;
      try {
        const isAbsolute = /^(https?:)?\/\//i.test(maybeUrl);
        return isAbsolute ? maybeUrl : (resImgPatch + maybeUrl);
      } catch (e) {
        return resImgPatch + maybeUrl;
      }
    };

    const emitSelection = () => {
      const fileData = files.value.map(f => {
        const respUrl = f.response?.url ? resolveUrl(f.response.url) : null;
        const urlToSend = respUrl || f.preview;
        return {
          name: f.raw.name,
          url: urlToSend,
          selected: f.selected
        };
      });
      emit("update:selectedFiles", fileData);
      emit("update:selected-files", fileData);
      emit("selectedFiles", fileData);
      emit("selected-files", fileData);
    };

    const uploadFile = async (fileObj, index) => {
      // проверка размера
      if (fileObj.raw && fileObj.raw.size && fileObj.raw.size > maxSizeBytes) {
        files.value[index].status = "Ошибка";
        files.value[index].error = `Файл превышает ${props.maxSizeMB}MB`;
        emitSelection();
        return;
      }

      // проверка accept
      if (!isAcceptedFile(fileObj.raw)) {
        files.value[index].status = "Ошибка";
        files.value[index].error = `Недопустимый формат. Допустимо: ${allowedText.value || 'все форматы'}`;
        emitSelection();
        return;
      }

      // если уже успешно загружен — пропускаем
      if (fileObj.status === "Успешно" && fileObj.response?.url) return;

      const formData = new FormData();
      formData.append("file", fileObj.raw);

      try {
        files.value[index].status = "Загрузка...";
        const response = await axios.post(uploadUrl, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const serverUrl = response.data?.url || response?.data;
        const resolved = resolveUrl(serverUrl);

        files.value[index] = {
          ...files.value[index],
          status: "Успешно",
          response: { url: serverUrl },
          preview: resolved || files.value[index].preview
        };

        emitSelection();
      } catch (err) {
        files.value[index].status = "Ошибка";
        files.value[index].error = err?.message || String(err);
        emitSelection();
      }
    };

    const onDrop = (acceptedFiles) => {
      if (props.disabled) return;

      const validFiles = [];
      const invalidFiles = [];

      acceptedFiles.forEach(f => {
        // проверяем size + accept
        if (f && f.size && f.size > maxSizeBytes) {
          invalidFiles.push({ file: f, reason: `Файл > ${props.maxSizeMB}MB` });
        } else if (!isAcceptedFile(f)) {
          invalidFiles.push({ file: f, reason: `Недопустимый формат. Допустимо: ${allowedText.value || 'все форматы'}` });
        } else {
          validFiles.push(f);
        }
      });

      // сначала добавляем невалидные — чтобы показать ошибку
      invalidFiles.forEach(({ file, reason }) => {
        files.value.push({
          raw: file,
          status: "Ошибка",
          response: null,
          preview: isImageFileObj(file) ? URL.createObjectURL(file) : null,
          selected: false,
          error: reason
        });
      });

      // вычисляем доступные слоты
      const slots = props.maxFiles - files.value.length;

      if (slots <= 0) {
        // нет места — помечаем все подходящие как ошибка лимита
        validFiles.forEach((file) => {
          files.value.push({
            raw: file,
            status: "Ошибка",
            response: null,
            preview: isImageFileObj(file) ? URL.createObjectURL(file) : null,
            selected: false,
            error: `Превышен лимит файлов (макс. ${props.maxFiles} файлов)`
          });
        });
        emitSelection();
        return;
      }

      const toAdd = validFiles.slice(0, slots);
      const overflow = validFiles.slice(slots);

      // помечаем переполнение как ошибки
      overflow.forEach((file) => {
        files.value.push({
          raw: file,
          status: "Ошибка",
          response: null,
          preview: isImageFileObj(file) ? URL.createObjectURL(file) : null,
          selected: false,
          error: `Превышен лимит файлов (макс. ${props.maxFiles} файлов)`
        });
      });

      // Добавляем валидные (в пределах слотов) и запускаем загрузку
      toAdd.forEach((file) => {
        const fileObj = {
          raw: file,
          status: null,
          response: null,
          preview: isImageFileObj(file) ? URL.createObjectURL(file) : null,
          selected: false
        };
        const index = files.value.length;
        files.value.push(fileObj);
        uploadFile(fileObj, index);
      });

      // Если были валидные — отметим последний как выбранный
      if (toAdd.length > 0) {
        files.value.forEach(f => f.selected = false);
        files.value[files.value.length - 1].selected = true;
      }

      emitSelection();
    };

    const initializeFiles = () => {
      // ограничиваем initialFiles до maxFiles
      const allowed = Array.isArray(props.initialFiles) ? props.initialFiles.slice(0, props.maxFiles) : [];

      allowed.forEach(file => {
        files.value.push({
          raw: { name: file.name || 'Файл', type: file.type || 'application/octet-stream' },
          status: "Успешно",
          response: { url: file.url },
          preview: file.url,
          selected: false
        });
      });

      // если initialFiles были длиннее лимита — сообщим в консоль (или можно добавить ошибки в список)
      if (Array.isArray(props.initialFiles) && props.initialFiles.length > props.maxFiles) {
        // оставляем только первые maxFiles, остальные игнорируем (вы можете изменить поведение при необходимости)
        console.warn(`Upload component: initialFiles trimmed to maxFiles=${props.maxFiles}`);
      }

      if (files.value.length > 0) {
        files.value.forEach(f => f.selected = false);
        files.value[files.value.length - 1].selected = true;
        emitSelection();
      }
    };

    const selectFile = (index) => {
      files.value.forEach((f, i) => f.selected = i === index);
      emitSelection();
    };

    const removeFile = (index) => {
      const file = files.value[index];
      if (!file) return;

      // revoke blob URL если есть
      if (file.preview && typeof file.preview === 'string' && file.preview.startsWith('blob:')) {
        try { URL.revokeObjectURL(file.preview); } catch (e) {}
      }

      files.value.splice(index, 1);

      // Если после удаления нет выбранного — выберем последний элемент
      if (files.value.length > 0) {
        const anySelected = files.value.some(f => f.selected);
        if (!anySelected) {
          files.value[files.value.length - 1].selected = true;
        }
      }

      emitSelection();
    };

    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
      onDrop,
      accept: acceptString.value, // если undefined — все форматы
      maxSize: maxSizeBytes,
      maxFiles: props.maxFiles,
      disabled: props.disabled,
    });

    onMounted(() => initializeFiles());

    onBeforeUnmount(() => {
      files.value.forEach(f => {
        if (f.preview && typeof f.preview === 'string' && f.preview.startsWith('blob:')) {
          try { URL.revokeObjectURL(f.preview); } catch(e) {}
        }
      });
    });

    // возвращаем showDelete и showSelect, чтобы шаблон мог к ним обратиться
    return { files, getRootProps, getInputProps, open, isDragActive, isImageFileObj, resImgPatch, selectFile, allowedText, maxSizeMB: props.maxSizeMB, removeFile, showDelete: props.showDelete, showSelect: props.showSelect, maxFiles: props.maxFiles };
  }
};
</script>

<style scoped>
/* оставил стили как в предыдущих версиях, с небольшими дополнениями */
.dropzone-disabled {
  opacity: 0.6;
  pointer-events: none;
}
.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-radius: 0.375rem;
  background-color: var(--grey-1);
  margin-bottom: 0.5rem;
}
.file-info { display: flex; align-items: center; gap: 0.75rem; }

.file-error {
  color: #e53e3e;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

/* checkbox styles */
.checkbox-wrapper { display:flex; align-items:center; cursor:pointer; position:relative; }
.checkbox-wrapper input[type="checkbox"] { opacity:0; width:0; height:0; }
.custom-checkbox { width:1.25rem; height:1.25rem; border-radius:50%; border:2px solid var(--blue-1); display:inline-block; position:relative; transition:all .2s; }
.custom-checkbox::after { content:""; position:absolute; display:none; left: 4px; top: 1.5px; width:5px; height:9px; border:solid white; border-width:0 2px 2px 0; transform:rotate(45deg); }
.checkbox-wrapper input[type="checkbox"]:checked + .custom-checkbox { background-color: var(--blue-1); }
.checkbox-wrapper input[type="checkbox"]:checked + .custom-checkbox::after { display:block; }

.file-preview-wrapper { position: relative; width: 3.125rem; height: 3.125rem; display:flex; align-items:center; justify-content:center; }
.file-preview { 
  width: 100%; 
  height: 100%; 
  object-fit: contain; 
  border-radius: 0.5rem;     
  background-color: var(--grey-0);
  padding: 0.5rem; 
}

.loader { width: 2rem; height: 2rem; border: 3px solid #f3f3f3; border-top: 3px solid var(--blue-1); border-radius: 50%; animation: spin 1s linear infinite; }
@keyframes spin { 0%{ transform:rotate(0deg);} 100%{ transform:rotate(360deg);} }

.dd__container { margin: 0 auto; padding: 0rem; margin-top: 0rem; border-radius: 1rem; }
.dropzone { border: 0.125rem dashed var(--grey-0); border-radius: 0.5rem; padding: 1rem; text-align: center; cursor: pointer; transition: all 0.3s; background-color: var(--grey-1); }
.active-dropzone { background-color: #ebf8ff; border-color: #63b3ed; }
.dropzone-content { color: var(--color-text); }
.icon { width: 3rem; height: 3rem; margin-bottom: 1rem; color: #a0aec0; }
.link { color: #4299e1; text-decoration: underline; cursor: pointer; }
.file-list { margin-top: 1rem; }
.file-name { font-weight: 500; color: var(--color-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; width: 100%; max-width: 150px; }
.file-status { font-size: 0.8rem; margin-top: 0.25rem; }
.file-status.success { color: #38a169; }
.file-status.error { color: #e53e3e; }
.file-status.uploading { color: var(--blue-1); }
.dd { width: 100%; }

/* Новые стили для кнопки удаления и контейнера действий */
.file-actions { display:flex; align-items:center; gap:0.5rem; }
.delete-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.15rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  color: white;
  position: relative;
}
.delete-btn:hover { opacity: 0.5 }
</style>